import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import privateAxios from "../../utils/axiosPrivate";
import {
  getPartnerDashboardURL,
  getPartnerCustomersURL,
  getPartnerCustomerInvoicesURL,
  getPartnerCommissionURL,
  exportPartnerCustomersURL
} from "../../utils/constants/apiEndPoints";
import { PARTNER_CLIENTS } from "../../route/paths";
import PartnerPortalHeader from "./PartnerPortalHeader";
import ReferralStrip from "./ReferralStrip";
import AddCustomerModal from "./AddCustomerModal";
import InlineDrillDown from "./InlineDrillDown";
import { StageBadge } from "./StageBadge";
import { FT, fmt, statusIdToStage } from "./partnerPortalShared";
import "./partnerDashboard.scss";

const STAGE_TABS = [
  { id: "all", label: "All clients", filter: () => true },
  { id: "approved", label: "Approved", filter: (c) => c.stage === "approved" },
  { id: "pending", label: "Pending", filter: (c) => c.stage === "pending" },
  {
    id: "customer_dd",
    label: "Customer DD",
    filter: (c) => c.stage === "customer_dd"
  },
  {
    id: "not_approved",
    label: "Not approved",
    filter: (c) => c.stage === "not_approved"
  },
  { id: "archived", label: "Archived", filter: (c) => c.stage === "archived" }
];

const STAGE_SORT_ORDER = [
  "approved",
  "pending",
  "customer_dd",
  "not_approved",
  "archived"
];

// Short, locale-friendly date for the client table (e.g. "05 May 2026"); em dash when absent.
const shortDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-NZ", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

export default function PartnerDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [nextPayout, setNextPayout] = useState(null);
  const [lastPayout, setLastPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [tab, setTab] = useState("all");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [selected, setSelected] = useState(null);
  const [invoicesByCustomer, setInvoicesByCustomer] = useState({});
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  // Org-lead drill-down: null = whole organisation, otherwise a single sub-partner id.
  const [partnerFilter, setPartnerFilter] = useState(null);

  const clientsRef = useRef(null);
  const location = useLocation();

  // Append the org drill-down filter to a partner-portal URL when one is active.
  const withPartner = (url) =>
    partnerFilter ? `${url}?partnerId=${partnerFilter}` : url;

  useEffect(() => {
    if (location.hash === "#clients" && clientsRef.current) {
      clientsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash, loading]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashRes, custRes, commRes] = await Promise.all([
        privateAxios.get(withPartner(getPartnerDashboardURL)),
        privateAxios.get(withPartner(getPartnerCustomersURL)),
        privateAxios.get(withPartner(getPartnerCommissionURL)).catch(() => null)
      ]);
      setDashboard(dashRes.data?.data || null);
      setCustomers(custRes.data?.data || []);
      const summary = commRes?.data?.data;
      // "Next payout" must be a genuinely future payout, not a past projected
      // date that was never recorded as paid.
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const futureUpcoming = (summary?.upcoming || []).filter(
        (p) => p.paidOn && new Date(p.paidOn) >= startOfToday
      );
      setNextPayout(futureUpcoming[0] || null);
      setLastPayout(summary?.paid?.[0] || null);
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          "Couldn't load your dashboard. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Reload whenever the org drill-down filter changes (and on mount).
  useEffect(() => {
    loadData();
  }, [partnerFilter]);

  const enrichedCustomers = useMemo(
    () =>
      customers.map((c) => {
        const person = `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim();
        const business = c.businessName || c.displayName;
        const stage = statusIdToStage(c.status);
        return {
          id: String(c.id),
          name: business || person,
          contact: person || business,
          email: c.email,
          stage,
          joined: c.dateReferred,
          totalFunded: c.totalFunded ?? 0,
          commissionToDate: c.commissionToDate ?? 0,
          referredBy: c.referredByPartnerName || "—"
        };
      }),
    [customers]
  );

  const tabCount = (tb) => enrichedCustomers.filter(tb.filter).length;

  const filteredRows = useMemo(() => {
    let list = enrichedCustomers.filter(
      STAGE_TABS.find((tb) => tb.id === tab).filter
    );
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          (c.contact || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [enrichedCustomers, tab, search]);

  const sortedRows = useMemo(() => {
    const valueOf = (c) => {
      switch (sortKey) {
        case "contact":
          return (c.contact || "").toLowerCase();
        case "referredBy":
          return (c.referredBy || "").toLowerCase();
        case "stage":
          return STAGE_SORT_ORDER.indexOf(c.stage);
        case "totalFunded":
          return c.totalFunded || 0;
        case "commission":
          return c.commissionToDate || 0;
        case "joined":
          return c.joined ? new Date(c.joined).getTime() : 0;
        case "name":
        default:
          return c.name.toLowerCase();
      }
    };
    return [...filteredRows].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredRows, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(k);
      setSortDir(["name", "contact", "stage"].includes(k) ? "asc" : "desc");
    }
  };

  const handleExport = async () => {
    try {
      const res = await privateAxios.get(
        withPartner(exportPartnerCustomersURL),
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `partner-clients-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Couldn't export clients. Please try again.");
    }
  };

  // Lazily load the invoice ledger the first time a row is expanded.
  const handleRowClick = async (customerId) => {
    if (selected === customerId) {
      setSelected(null);
      return;
    }
    setSelected(customerId);
    if (!invoicesByCustomer[customerId]) {
      setInvoicesByCustomer((prev) => ({
        ...prev,
        [customerId]: { loading: true }
      }));
      try {
        const res = await privateAxios.get(
          getPartnerCustomerInvoicesURL(customerId)
        );
        setInvoicesByCustomer((prev) => ({
          ...prev,
          [customerId]: { loading: false, invoices: res.data?.data || [] }
        }));
      } catch {
        setInvoicesByCustomer((prev) => ({
          ...prev,
          [customerId]: { loading: false, invoices: [], error: true }
        }));
      }
    }
  };

  const approvedCount = dashboard?.approvedCount ?? 0;
  const totalCount = dashboard?.totalCustomers ?? enrichedCustomers.length;
  const inPipeline = dashboard?.inPipelineCount ?? 0;
  const today = useMemo(() => new Date(), []);
  const daysToPayout = nextPayout?.paidOn
    ? Math.max(0, Math.ceil((new Date(nextPayout.paidOn) - today) / 86400000))
    : null;
  const pctOfLast =
    nextPayout && lastPayout && lastPayout.amount > 0
      ? Math.min(120, Math.round((nextPayout.amount / lastPayout.amount) * 100))
      : 0;
  const paceLabel = pctOfLast >= 100 ? "· ahead" : "· tracking";

  const firstName = dashboard?.partnerName?.split(/\s+/)[0] || "there";
  // Pass the real name (empty while loading) — the header caches it and shows a
  // neutral avatar until it resolves, rather than flashing a placeholder initial.
  const partnerName = dashboard?.partnerName || "";

  // Organisation head-contact view: the logged-in partner is an org lead, so we
  // surface the per-partner breakdown, a drill-down filter, and a "Referred by" column.
  const isLead = !!dashboard?.organisationLead;
  const members = dashboard?.members || [];
  // This component serves two tabs: the Dashboard (hero only) and the
  // Clients/Partners list. The route decides which half renders.
  const isClientsRoute = location.pathname.startsWith(PARTNER_CLIENTS);
  const gridCols = isLead
    ? "1.6fr 1.3fr 1.2fr 1.1fr 1.1fr 1.2fr 1.2fr 24px"
    : "1.6fr 1.3fr 1.1fr 1.1fr 1.2fr 1.2fr 24px";
  const activeMemberName =
    partnerFilter &&
    members.find((m) => m.partnerId === partnerFilter)?.partnerName;

  let signupCopyUrl;
  if (dashboard?.signupPageUrl) {
    signupCopyUrl = dashboard.signupPageUrl;
  } else if (dashboard?.referralCode) {
    signupCopyUrl = `https://fundtap.co/partners/${dashboard.referralCode.toLowerCase()}`;
  } else {
    signupCopyUrl = "https://fundtap.co/partners/your-code";
  }
  const signupDisplayUrl = signupCopyUrl.replace(/^https?:\/\//, "");

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        background: FT.paperWarm,
        color: FT.navy,
        fontFamily: "DM Sans, system-ui, sans-serif",
        position: "relative"
      }}
    >
      <PartnerPortalHeader
        partnerName={partnerName}
        referralCode={dashboard?.referralCode}
        signupPageUrl={dashboard?.signupPageUrl}
        isLead={isLead}
        onAddClient={() => setAddOpen(true)}
      />

      {loading && (
        <div style={{ padding: "60px 56px", color: FT.navy60, fontSize: 14 }}>
          Loading…
        </div>
      )}
      {error && (
        <div
          style={{
            margin: "28px 56px",
            padding: "12px 16px",
            background: "#FBE6E6",
            border: "1px solid #F5C2C2",
            color: "#9B2C2C",
            borderRadius: 10,
            fontSize: 13
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Persistent referral sign-up link — first element of content on every tab. */}
          <section style={{ padding: "28px 56px 0" }}>
            <ReferralStrip
              signupPageUrl={dashboard?.signupPageUrl}
              referralCode={dashboard?.referralCode}
            />
          </section>

          {!isClientsRoute && (
            <section style={{ padding: "0 56px 0" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  marginBottom: 20
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 12,
                      color: FT.navy60,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: "uppercase"
                    }}
                  >
                    Partner dashboard
                  </div>
                  <h1
                    style={{
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                      fontSize: 34,
                      fontWeight: 800,
                      margin: "6px 0 0",
                      letterSpacing: -0.8,
                      color: FT.navy,
                      lineHeight: 1.15
                    }}
                  >
                    Kia ora, {firstName}
                  </h1>
                  <div
                    style={{
                      fontSize: 13.5,
                      color: FT.navy60,
                      marginTop: 6
                    }}
                  >
                    You&apos;re earning on{" "}
                    <span style={{ color: FT.navy, fontWeight: 700 }}>
                      {approvedCount} of {totalCount}
                    </span>{" "}
                    clients.{" "}
                    <span style={{ color: FT.navy, fontWeight: 700 }}>
                      {inPipeline}
                    </span>{" "}
                    more are working through onboarding.
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 22 }}>
                <div
                  style={{
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 16,
                    padding: "28px 32px",
                    background: FT.navy,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    gap: 48
                  }}
                >
                  <div style={{ flex: "0 0 auto", minWidth: 0 }}>
                    <span
                      style={{
                        fontSize: 11.5,
                        color: FT.mint,
                        fontWeight: 800,
                        letterSpacing: 0.8,
                        textTransform: "uppercase"
                      }}
                    >
                      Expected next payout
                    </span>
                    <div
                      style={{
                        fontSize: 52,
                        fontWeight: 800,
                        marginTop: 8,
                        letterSpacing: -1.8,
                        lineHeight: 1
                      }}
                    >
                      {fmt.money(nextPayout?.amount || 0)}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginTop: 14,
                        fontSize: 13,
                        color: "rgba(255,255,255,0.75)",
                        flexWrap: "wrap"
                      }}
                    >
                      <span>
                        Pays{" "}
                        <b style={{ color: "#fff" }}>
                          {nextPayout ? fmt.date(nextPayout.paidOn) : "—"}
                        </b>
                      </span>
                      <span
                        style={{
                          width: 3,
                          height: 3,
                          borderRadius: 99,
                          background: "rgba(255,255,255,0.35)"
                        }}
                      />
                      <span>
                        <b style={{ color: "#fff" }}>{daysToPayout ?? "—"}</b>{" "}
                        {daysToPayout === 1 ? "day" : "days"} to go
                      </span>
                      <span
                        style={{
                          width: 3,
                          height: 3,
                          borderRadius: 99,
                          background: "rgba(255,255,255,0.35)"
                        }}
                      />
                      <span>
                        <b style={{ color: "#fff" }}>
                          {nextPayout?.invoiceCount ?? 0}
                        </b>{" "}
                        invoices
                      </span>
                      <span
                        style={{
                          width: 3,
                          height: 3,
                          borderRadius: 99,
                          background: "rgba(255,255,255,0.35)"
                        }}
                      />
                      <span>
                        <b style={{ color: "#fff" }}>
                          {nextPayout?.customerCount ?? 0}
                        </b>{" "}
                        clients
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      gap: 8,
                      position: "relative",
                      zIndex: 1,
                      minWidth: 0
                    }}
                  >
                    <div
                      style={{
                        height: 8,
                        borderRadius: 99,
                        background: "rgba(255,255,255,0.1)",
                        overflow: "hidden"
                      }}
                    >
                      <div
                        style={{
                          width: Math.min(100, pctOfLast) + "%",
                          height: "100%",
                          background: FT.mint
                        }}
                      />
                    </div>
                    <div
                      style={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }}
                    >
                      {lastPayout
                        ? `${pctOfLast}% of last month's payout ${paceLabel}`
                        : "No prior payouts to compare against yet"}
                    </div>
                  </div>
                  <div
                    aria-hidden
                    style={{
                      position: "absolute",
                      right: -60,
                      bottom: -80,
                      width: 260,
                      height: 260,
                      borderRadius: 99,
                      background:
                        "radial-gradient(circle at 30% 30%, rgba(111,216,159,0.16), transparent 65%)"
                    }}
                  />
                </div>
              </div>
            </section>
          )}

          {isClientsRoute && isLead && members.length > 0 && (
            <section style={{ padding: "0 56px 0" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 12
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: FT.navy60,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: "uppercase"
                  }}
                >
                  Your organisation
                  {dashboard?.organisationName
                    ? ` · ${dashboard.organisationName}`
                    : ""}
                </div>
                {partnerFilter && (
                  <button
                    onClick={() => setPartnerFilter(null)}
                    style={{
                      border: `1px solid ${FT.navy15}`,
                      borderRadius: 8,
                      padding: "6px 12px",
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: FT.navy,
                      background: "#fff",
                      cursor: "pointer",
                      fontFamily: "inherit"
                    }}
                  >
                    {activeMemberName
                      ? `Viewing ${activeMemberName} — show all`
                      : "Show all partners"}
                  </button>
                )}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(auto-fill, minmax(220px, 1fr))`,
                  gap: 12,
                  marginBottom: 22
                }}
              >
                {members.map((m) => {
                  const active = partnerFilter === m.partnerId;
                  return (
                    <button
                      key={m.partnerId}
                      onClick={() =>
                        setPartnerFilter(active ? null : m.partnerId)
                      }
                      style={{
                        textAlign: "left",
                        border: active
                          ? `1.5px solid ${FT.mintDeep}`
                          : `1px solid ${FT.navy08}`,
                        borderRadius: 14,
                        padding: "16px 18px",
                        background: active ? FT.mintBg : "#fff",
                        cursor: "pointer",
                        fontFamily: "inherit"
                      }}
                    >
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: FT.navy,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                      >
                        {m.partnerName}
                      </div>
                      <div
                        style={{ fontSize: 12, color: FT.navy60, marginTop: 2 }}
                      >
                        {m.referralCode}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 16,
                          marginTop: 12,
                          fontSize: 12.5,
                          color: FT.navy80
                        }}
                      >
                        <span>
                          <b style={{ color: FT.navy }}>{m.totalCustomers}</b>{" "}
                          clients
                        </span>
                        <span>
                          <b style={{ color: FT.navy }}>{m.approvedCount}</b>{" "}
                          earning
                        </span>
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          fontSize: 15,
                          fontWeight: 800,
                          color: FT.navy,
                          fontVariantNumeric: "tabular-nums"
                        }}
                      >
                        {fmt.money(m.totalCommissionEarned || 0)}
                        <span
                          style={{
                            fontSize: 11.5,
                            fontWeight: 600,
                            color: FT.navy60
                          }}
                        >
                          {" "}
                          commission
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {isClientsRoute && (
            <section
              ref={clientsRef}
              id="clients"
              style={{ padding: "0 56px 56px", scrollMarginTop: 80 }}
            >
              <div
                style={{
                  border: `1px solid ${FT.navy08}`,
                  borderRadius: 16,
                  overflow: "hidden",
                  background: "#fff"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "14px 20px 0",
                    flexWrap: "wrap",
                    gap: 8
                  }}
                >
                  <div style={{ display: "flex", gap: 4, flexWrap: "nowrap" }}>
                    {STAGE_TABS.map((tb) => (
                      <button
                        key={tb.id}
                        onClick={() => setTab(tb.id)}
                        style={{
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          padding: "10px 14px",
                          fontFamily: "inherit",
                          fontSize: 14,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          color: tab === tb.id ? FT.navy : FT.navy60,
                          borderBottom:
                            tab === tb.id
                              ? `2px solid ${FT.mint}`
                              : "2px solid transparent",
                          position: "relative",
                          top: 1
                        }}
                      >
                        {tb.label}{" "}
                        <span
                          style={{
                            color: FT.navy40,
                            fontWeight: 600,
                            marginLeft: 4
                          }}
                        >
                          {tabCount(tb)}
                        </span>
                      </button>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8, paddingBottom: 8 }}>
                    <div
                      style={{
                        border: `1px solid ${FT.navy15}`,
                        borderRadius: 8,
                        padding: "7px 10px",
                        fontSize: 13,
                        color: FT.navy60,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        minWidth: 200
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="11" cy="11" r="7" />
                        <path d="m20 20-3.5-3.5" />
                      </svg>
                      <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search clients"
                        style={{
                          border: "none",
                          outline: "none",
                          fontFamily: "inherit",
                          fontSize: 13,
                          color: FT.navy,
                          background: "transparent",
                          flex: 1,
                          padding: 0
                        }}
                      />
                    </div>
                    <button
                      onClick={handleExport}
                      style={{
                        border: `1px solid ${FT.navy15}`,
                        borderRadius: 8,
                        padding: "7px 12px",
                        fontSize: 13,
                        color: FT.navy,
                        background: "#fff",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontWeight: 600
                      }}
                    >
                      Export
                    </button>
                    <button
                      onClick={() => setAddOpen(true)}
                      style={{
                        border: "none",
                        borderRadius: 10,
                        height: 40,
                        padding: "0 18px",
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: "#fff",
                        background: FT.navy,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        whiteSpace: "nowrap"
                      }}
                    >
                      Add client
                    </button>
                  </div>
                </div>

                <div style={{ borderTop: `1px solid ${FT.navy08}` }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: gridCols,
                      padding: "12px 20px",
                      fontSize: 11.5,
                      color: FT.navy60,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: "uppercase",
                      borderBottom: `1px solid ${FT.navy08}`,
                      background: FT.navy04
                    }}
                  >
                    {[
                      { k: "name", label: "Name", align: "left" },
                      { k: "contact", label: "Contact", align: "left" },
                      ...(isLead
                        ? [
                            {
                              k: "referredBy",
                              label: "Referred by",
                              align: "left"
                            }
                          ]
                        : []),
                      { k: "stage", label: "Stage", align: "left" },
                      { k: "joined", label: "Created", align: "left" },
                      {
                        k: "totalFunded",
                        label: "Funded to date",
                        align: "right"
                      },
                      {
                        k: "commission",
                        label: "Commission to date",
                        align: "right"
                      }
                    ].map((h) => {
                      const active = sortKey === h.k;
                      return (
                        <div
                          key={h.k}
                          onClick={() => toggleSort(h.k)}
                          style={{
                            cursor: "pointer",
                            userSelect: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            justifyContent:
                              h.align === "right" ? "flex-end" : "flex-start",
                            color: active ? FT.navy : FT.navy60
                          }}
                        >
                          {h.align === "right" && active && (
                            <span
                              style={{
                                fontSize: 9,
                                lineHeight: 1,
                                color: FT.mintDeep
                              }}
                            >
                              {sortDir === "asc" ? "▲" : "▼"}
                            </span>
                          )}
                          <span>{h.label}</span>
                          {h.align !== "right" && active && (
                            <span
                              style={{
                                fontSize: 9,
                                lineHeight: 1,
                                color: FT.mintDeep
                              }}
                            >
                              {sortDir === "asc" ? "▲" : "▼"}
                            </span>
                          )}
                        </div>
                      );
                    })}
                    <div />
                  </div>

                  {sortedRows.map((c, i) => {
                    const drill = invoicesByCustomer[c.id];
                    return (
                      <React.Fragment key={c.id}>
                        <div
                          onClick={() => handleRowClick(c.id)}
                          style={{
                            display: "grid",
                            gridTemplateColumns: gridCols,
                            padding: "14px 20px",
                            alignItems: "center",
                            borderBottom:
                              selected !== c.id && i === sortedRows.length - 1
                                ? "none"
                                : `1px solid ${FT.navy08}`,
                            cursor: "pointer",
                            background: selected === c.id ? FT.mintBg : "#fff",
                            transition: "background 0.12s"
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 14,
                              color: FT.navy
                            }}
                          >
                            {c.name}
                          </div>
                          <div style={{ fontSize: 13.5, color: FT.navy80 }}>
                            {c.contact || "—"}
                          </div>
                          {isLead && (
                            <div style={{ fontSize: 13, color: FT.navy60 }}>
                              {c.referredBy}
                            </div>
                          )}
                          <div>
                            <StageBadge stage={c.stage} />
                          </div>
                          <div className="pp-table-cell--muted">
                            {shortDate(c.joined)}
                          </div>
                          <div
                            style={{
                              textAlign: "right",
                              fontSize: 13.5,
                              fontVariantNumeric: "tabular-nums",
                              color: c.totalFunded ? FT.navy : FT.navy40,
                              fontWeight: 600
                            }}
                          >
                            {c.totalFunded ? fmt.money0(c.totalFunded) : "—"}
                          </div>
                          <div
                            style={{
                              textAlign: "right",
                              fontSize: 13.5,
                              fontVariantNumeric: "tabular-nums",
                              color: c.commissionToDate ? FT.navy : FT.navy40,
                              fontWeight: 700
                            }}
                          >
                            {c.commissionToDate
                              ? fmt.money(c.commissionToDate)
                              : "—"}
                          </div>
                          <div
                            style={{
                              color: FT.navy40,
                              textAlign: "right",
                              transform:
                                selected === c.id ? "rotate(90deg)" : "none",
                              transition: "transform 0.18s"
                            }}
                          >
                            ›
                          </div>
                        </div>
                        {selected === c.id && (
                          <InlineDrillDown
                            customer={c}
                            loading={drill?.loading}
                            invoices={drill?.invoices || []}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}

                  {sortedRows.length === 0 && (
                    <div
                      style={{
                        padding: "40px 20px",
                        textAlign: "center",
                        color: FT.navy60,
                        fontSize: 14
                      }}
                    >
                      No clients match.
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {addOpen && (
        <AddCustomerModal
          signupUrl={signupDisplayUrl}
          signupCopyUrl={signupCopyUrl}
          onClose={() => setAddOpen(false)}
          onSuccess={() => loadData()}
        />
      )}
    </div>
  );
}
